import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { faHeadphonesAlt, faVolumeUp, faSyncAlt, faEyeSlash, faEye } from '@fortawesome/free-solid-svg-icons';
import { UserService as CUserService } from 'app/layouts/homepage/services/user.service';
import { ChallengeAnswer, ChallengeQuestion, ForgotData, IForgotData } from 'app/core/user/updateObject.model';
// @ts-ignore
import { v4 as uuidv4 } from 'uuid';
import { DataSharingService } from 'app/layouts/homepage/services/data-sharing.service';
import { CAPTCHA_ENABLED } from 'app/app.constants';
import { HttpErrorResponse } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'jhi-forgot-password',
    templateUrl: './forgot-password.component.html',
    styleUrls: ['../../../../../content/scss/styles.scss']
})
export class ForgotPasswordComponent implements OnInit {
    passwordForm: FormGroup;
    submitted = false;
    appUrl = '';
    faHeadphones = faHeadphonesAlt;
    faVolumeUp = faVolumeUp;
    faSyncAlt = faSyncAlt;
    faEyeSlash = faEyeSlash;
    faEye = faEye;
    successMsg: string;
    show = false;
    imageToShow: string | ArrayBuffer;
    private captchaId = '';
    captchaPassed = false;
    captchaEnabled = CAPTCHA_ENABLED;  // Read from build-time constant
    confirmData: ChallengeQuestion;
    showAnswer = false;
    errorMsg = '';
    channelEmail = false;
    channelEmailAlt = true;
    channelSMS = false;
    channelToBeUsed: string;
    defaultRadioValue: string;
    errorCheckStepTwo = false;
    errorCheckStepOne = false;

    constructor(
        private formBuilder: FormBuilder,
        private route: ActivatedRoute,
        private cUserService: CUserService,
        private router: Router,
        private dataSharingService: DataSharingService,
        private translateService: TranslateService
    ) {
        this.createForm();
        this.appUrl = this.route.snapshot.queryParamMap.get('appUrl');
    }

    ngOnInit() {
        // Conditionally load captcha based on build-time constant
        console.log('=== CAPTCHA DEBUG (FORGOT PASSWORD) ===');
        console.log('captchaEnabled:', this.captchaEnabled);
        console.log('CAPTCHA_ENABLED constant:', CAPTCHA_ENABLED);
        
        if (this.captchaEnabled) {
            console.log('Calling getCaptcha()...');
            this.getCaptcha();
        } else {
            console.log('Captcha DISABLED - clearing validators');
            // If captcha is disabled, remove validation and mark as passed
            this.passwordForm.get('captcha').clearValidators();
            this.passwordForm.get('captcha').updateValueAndValidity();
            this.captchaPassed = true;
        }
    }

    private createForm() {
        this.passwordForm = this.formBuilder.group({
            username: ['', [Validators.required]],
            captcha: ['', [Validators.required]],
            question: [''],
            answer: [{ disabled: true, value: '' }, [Validators.required]],
            invio: [{ value: '' }, [Validators.required]]
        });
    }

    get f() {
        return this.passwordForm.controls;
    }

    get fUsername() {
        return this.passwordForm.get('username');
    }

    get fCaptcha() {
        return this.passwordForm.get('captcha');
    }

    get fQuestion() {
        return this.passwordForm.get('question');
    }

    get fAnswer() {
        return this.passwordForm.get('answer');
    }

    get radioChoice() {
        return this.passwordForm.get('invio');
    }

    onSubmit(event: any) {
        if (!event) {
            // fix firefox double submit
            return;
        }

        if (this.confirmData && this.passwordForm.controls['answer'].value !== '') {
            if (this.passwordForm.controls['answer'].value === null) {
                this.errorCheckStepTwo = true;
            } else {
                this.sendRequest();
                return;
            }
        }
        if (this.confirmData && this.passwordForm.controls['answer'].value === '') {
            this.errorCheckStepTwo = true;
        }
        this.errorCheckStepOne = true;
        // stop here if form is invalid
        if (this.passwordForm.invalid) {
            return;
        }
        this.submitted = true;
        const outObj: IForgotData = new ForgotData(null, this.fUsername.value, this.fCaptcha.value, this.captchaId);
        
        // CHECK PREVENTIVO: verifica se l'utente può procedere (passwordless + EXTRA_UE)
        console.log('=== PRE-CHECK: Verifying if user can proceed ===');
        console.log('Username:', this.fUsername.value);
        
        this.cUserService
            .checkPasswordless(outObj)
            .toPromise()
            .then(checkRes => {
                console.log('Pre-check response:', checkRes);
                if (checkRes && checkRes.body && checkRes.body.canProceed === false) {
                    // Utente non può procedere - mostra messaggio e non fare altre chiamate
                    console.log('User CANNOT proceed - blocking');
                    this.errorMsg = this.translateService.instant('error.functionality.not.available');
                    this.submitted = false;
                    return Promise.reject({ skipChallengeCall: true });
                }
                console.log('User CAN proceed - calling getChallengeQuestion');
                // Utente può procedere - chiama getChallengeQuestion
                return this.cUserService.proceedForgotCredential(outObj).toPromise();
            })
            .then(res => {
                if (res && res.body && res.body) {
                    this.confirmData = res.body;
                    this.patchForm();
                    this.fAnswer.enable();
                } else {
                    this.successMsg = 'Operazione effettuata con successo';
                }
            })
            .catch(err => {
                // Se è stato bloccato dal pre-check, non fare nulla (messaggio già impostato)
                if (err && err.skipChallengeCall) {
                    console.log('Skipped challenge call due to pre-check failure');
                    return;
                }
                
                console.log('=== ERROR in proceedForgotCredential ===');
                console.log('Full error object:', err);
                if (err && err.error) {
                    console.log('err.error:', err.error);
                    console.log('err.error.message:', err.error.message);
                    this.errorMsg = err.error.message;
                    console.log('this.errorMsg set to:', this.errorMsg);
                    this.getCaptcha();
                } else {
                    console.log('Error structure not recognized');
                }
            })
            .finally(() => {
                this.submitted = false;
            });
    }

    onReset() {
        if (!this.confirmData) {
            this.passwordForm.controls['username'].reset();
            this.passwordForm.controls['captcha'].reset();
            this.errorCheckStepOne = false;
        }
        if (this.confirmData) {
            this.passwordForm.controls['answer'].reset();
            this.errorCheckStepTwo = false;
        }
        // this.submitted = false;
    }

    backToApp() {
        this.router.navigate(['/']);
    }

    createImageFromBlob(image: Blob) {
        const reader = new FileReader();
        reader.addEventListener(
            'load',
            () => {
                this.imageToShow = reader.result;
            },
            false
        );

        if (image) {
            reader.readAsDataURL(image);
        }
    }

    getCaptcha() {
        this.captchaId = uuidv4();
        console.log('getCaptcha() - captchaId:', this.captchaId);
        this.cUserService
            .requestCaptcha(this.captchaId)
            .toPromise()
            .then(res => {
                console.log('Captcha response received:', res);
                this.createImageFromBlob(res.body);
            })
            .catch(err => {
                console.error('Error loading captcha:', err);
            });
    }

    getCaptchaSound() {
        this.cUserService
            .requestCaptchaSound(this.captchaId)
            .toPromise()
            .then(res => {
                const audio = new Audio(res.url);
                audio.play();
            })
            .catch(err => {
                // console.log(err);
            });
    }

    changeShowAnswer() {
        this.showAnswer = !this.showAnswer;
    }

    sendRequest() {
        this.submitted = true;
        this.channelToBeUsed = this.radioChoice.value;
        this.cUserService
            .confirmForgotPasswordRequest(
                new ChallengeAnswer(null, this.fUsername.value, this.fAnswer.value, this.channelToBeUsed, this.confirmData.tempUUID)
            )
            .toPromise()
            .then(res => {
                if (res && res.body && res.body.message) {
                    this.successMsg = res.body.message;
                } else {
                    this.successMsg = 'Operazione effettuata con successo';
                }
            })
            .catch(err => {
                this.verifyValidationError(err);
            })
            .finally(() => {
                this.submitted = false;
            });
    }

    verifyValidationError(err: HttpErrorResponse) {
        if (err && err.error) {
            if (err.error.message === 'error.validation') {
                this.errorMsg = 'Si è verificato un errore nella validazione dei campi.';
            } else {
                this.errorMsg = err.error.message;
            }
        }
    }

    private patchForm() {
        this.fUsername.patchValue(this.confirmData.userId);
        this.fQuestion.patchValue(this.confirmData.challengeQuestion);
        this.channelEmail = this.confirmData.validEmail;
        this.channelEmailAlt = this.confirmData.validAltEmail;
        this.channelSMS = this.confirmData.validSMS;
        if (this.channelEmail === true) {
            this.defaultRadioValue = 'EMAIL';
        } else if (this.channelSMS === true) {
            this.defaultRadioValue = 'SMS';
        } else if (this.channelEmailAlt === true) {
            this.defaultRadioValue = 'ALT_EMAIL';
        } else {
            this.defaultRadioValue = null;
        }
        this.radioChoice.patchValue(this.defaultRadioValue);
    }

    get isDesktop(): boolean {
        return this.dataSharingService.isDesktop();
    }
}
